import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaPlus,
  FaDownload,
  FaImages,
  FaTrash,
  FaCheck,
  FaArrowRightArrowLeft,
} from 'react-icons/fa6';
import { FaEdit, FaLock, FaLockOpen } from 'react-icons/fa';
import { TbChevronLeft, TbChevronRight } from 'react-icons/tb';
import { toast } from 'react-toastify';
import PageHeader from '../../components/misc/PageHeader';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import Button from '../../components/buttons/Button';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import EditAlbumModal from '../../components/modals/EditAlbumModal';
import AddPhotosModal from '../../components/modals/AddPhotosModal';
import PhotoViewerModal from '../../components/modals/PhotoViewerModal';
import MovePhotoModal from '../../components/modals/MovePhotoModal';
import AreYouSureModal from '../../components/modals/AreYouSureModal';
import photoAlbumService from '../../../services/community/photoAlbumService';
import ApiImage from '../../components/misc/ApiImage';
import { isVideoFile } from '../../../utils/mediaUtils';

interface PhotoCardProps {
  photo: Photo;
  showMetadata: boolean;
  onClick: (photo: Photo) => void;
  isSelected: boolean;
  onSelectionChange: (photoId: number, isSelected: boolean) => void;
  isSelectionMode: boolean;
}

function PhotoCard({
  photo,
  showMetadata,
  onClick,
  isSelected,
  onSelectionChange,
  isSelectionMode,
}: PhotoCardProps): JSX.Element {
  const isVideo = isVideoFile(photo.pictureUrl);

  const handleClick = () => {
    if (isSelectionMode) {
      onSelectionChange(photo.id, !isSelected);
    } else {
      onClick(photo);
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onSelectionChange(photo.id, e.target.checked);
  };

  return (
    <div
      className={`group relative cursor-pointer overflow-hidden rounded-lg bg-secondary-200 bg-opacity-50 transition-all hover:bg-opacity-80 hover:shadow-md ${isSelected ? 'ring-2 ring-primary-900' : ''}`}>
      {isSelectionMode && (
        <div className="absolute left-2 top-2 z-10">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={handleCheckboxChange}
            className="h-5 w-5 rounded border-primary-900 accent-primary-900 checked:border-primary-900 checked:bg-primary-900 checked:text-primary-900 focus:ring-2 focus:ring-primary-900"
          />
        </div>
      )}

      <div className="relative h-32 w-full overflow-hidden rounded-t-lg" onClick={handleClick}>
        {isVideo ? (
          <>
            <video
              src={photo.pictureUrl}
              className="h-full w-full object-cover transition-transform group-hover:scale-105"
              controls={false}
              autoPlay={false}
              preload="metadata">
              <track kind="captions" />
              Your browser does not support the video tag.
            </video>
            {/* Video play indicator */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="rounded-full bg-black bg-opacity-60 p-2">
                <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </>
        ) : (
          <ApiImage
            path={photo.pictureUrl}
            alt={`Photo-${photo.createdAt}`}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        )}
      </div>

      {showMetadata && (
        <div className="p-2">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="truncate">
              {photo.uploader.firstName} {photo.uploader.lastName}
            </span>
            <span className="text-xs">{new Date(photo.createdAt).toLocaleDateString()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PhotoAlbumPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { albumId } = useParams<{ albumId: string }>();
  const [album, setAlbum] = useState<PhotoAlbumWithItems | undefined>(undefined);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addPhotosModalOpen, setAddPhotosModalOpen] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [movePhotosModalOpen, setMovePhotosModalOpen] = useState(false);
  const [deletePhotosModalOpen, setDeletePhotosModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<number[]>([]);
  const PHOTOS_PER_PAGE = 20;

  if (!albumId) {
    navigate('/albums');
    return <></>;
  }

  const fetchAlbum = async (page: number = 1) => {
    try {
      const startIndex = (page - 1) * PHOTOS_PER_PAGE;
      const endIndex = startIndex + PHOTOS_PER_PAGE;
      const fetchedAlbum = await photoAlbumService.getPhotoAlbumById(albumId, startIndex, endIndex);

      setAlbum(fetchedAlbum);
    } catch (error) {
      console.error('Failed to fetch album:', error);
      if (page === 1) {
        navigate('/albums');
      }
    }
  };

  useEffect(() => {
    fetchAlbum(1);
    setCurrentPage(1);
    setSelectedPhotoIds([]);
    setIsSelectionMode(false);
  }, [albumId, navigate]);

  const handleSelectionChange = (photoId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedPhotoIds((prev) => [...prev, photoId]);
    } else {
      setSelectedPhotoIds((prev) => prev.filter((id) => id !== photoId));
    }
  };

  const handleSelectAll = () => {
    if (!album) return;

    if (selectedPhotoIds.length === album.items.length) {
      setSelectedPhotoIds([]);
    } else {
      setSelectedPhotoIds(album.items.map((photo) => photo.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedPhotoIds([]);
    setIsSelectionMode(false);
  };

  const handleDeleteSelectedPhotos = async () => {
    if (!album || !selectedPhotoIds.length) return;

    try {
      await photoAlbumService.deletePhotos(album.id, selectedPhotoIds);

      const updatedPhotos = album.items.filter((photo) => !selectedPhotoIds.includes(photo.id));
      setAlbum({
        ...album,
        items: updatedPhotos,
        photoCount: Math.max(0, album.photoCount - selectedPhotoIds.length),
      });

      toast.success(t('page.photoAlbum.deletePhotos.success', { count: selectedPhotoIds.length }));
      handleClearSelection();
    } catch (error) {
      console.error('Error deleting photos:', error);
      toast.error(t('toast.error.unexpected'));
    }
  };

  const handlePhotosMove = (photoIds: number | number[]) => {
    if (album) {
      const idsArray = Array.isArray(photoIds) ? photoIds : [photoIds];
      const updatedPhotos = album.items.filter((photo) => !idsArray.includes(photo.id));
      setAlbum({
        ...album,
        items: updatedPhotos,
        photoCount: Math.max(0, album.photoCount - idsArray.length),
      });

      if (selectedPhoto && idsArray.includes(selectedPhoto.id)) {
        setSelectedPhoto(null);
        setPhotoViewerOpen(false);
      }

      handleClearSelection();
    }
  };

  if (!album) {
    return <></>;
  }

  const totalPhotos = album.photoCount;
  const displayedPhotos = album.items.length;
  const totalPages = Math.ceil(totalPhotos / PHOTOS_PER_PAGE);

  const handlePhotoClick = (photo: Photo) => {
    setSelectedPhoto(photo);
    setPhotoViewerOpen(true);
  };

  const handlePhotoDelete = async (photoId: number) => {
    if (album) {
      try {
        await photoAlbumService.deletePhoto(album.id, photoId);

        const updatedPhotos = album.items.filter((photo) => photo.id !== photoId);
        setAlbum({
          ...album,
          items: updatedPhotos,
          photoCount: Math.max(0, album.photoCount - 1),
        });

        if (selectedPhoto && selectedPhoto.id === photoId) {
          setSelectedPhoto(null);
          setPhotoViewerOpen(false);
        }

        toast.success(t('page.photoAlbum.photoDeleted'));
      } catch (error) {
        console.error('Error deleting photo:', error);
      }
    }
  };

  const handlePhotosAdded = (newPhotos: Photo[]) => {
    if (album) {
      setAlbum({
        ...album,
        items: [...newPhotos, ...album.items],
        photoCount: album.photoCount + newPhotos.length,
      });
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchAlbum(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadAlbum = async () => {
    if (!albumId) return;
    setIsDownloading(true);
    const toastId = toast.info(t('page.photoAlbum.preparing'), { autoClose: false });
    try {
      const blob = await photoAlbumService.getPhotoAlbumZip(albumId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${album?.title}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading album:', error);
    }
    setIsDownloading(false);
    toast.dismiss(toastId);
  };

  const handleAlbumUpdate = (updatedAlbum: Partial<PhotoAlbum>) => {
    if (album) {
      setAlbum({ ...album, ...updatedAlbum });
    }
  };

  const handleAlbumDelete = async () => {
    if (!album) return;

    try {
      await photoAlbumService.deletePhotoAlbum(album.id);
      toast.success(t('page.photoAlbum.deleteAlbum.success'));
      navigate('/albums');
    } catch (error) {
      console.error('Error deleting album:', error);
    }
  };

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/albums')}
          className="rounded-full p-2 transition-colors hover:bg-primary-300 hover:bg-opacity-20">
          <FaArrowLeft className="h-5 w-5 text-primary-900" />
        </button>
      </div>

      <PageHeader
        title={album.title}
        subtitle={album.description}
        primaryButton={
          !isSelectionMode ? (
            <PrimaryButton
              text={t('page.photoAlbum.addPhotos')}
              icon={FaPlus}
              onClick={() => setAddPhotosModalOpen(true)}
            />
          ) : undefined
        }
      />

      <div className="mb-2 flex flex-col gap-2 rounded-xl bg-secondary-200 bg-opacity-50 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <FaImages className="h-4 w-4" />
              <span>
                {album.photoCount} {t('page.photoAlbum.photos')}
              </span>
            </div>
            <div
              className={`flex items-center gap-1 ${album.openToUpload ? 'text-success' : 'text-secondary'}`}>
              {album.openToUpload ? (
                <>
                  <FaLockOpen className="h-3 w-3" />
                  <span>{t('page.albums.openUpload')}</span>
                </>
              ) : (
                <>
                  <FaLock className="h-3 w-3" />
                  <span>{t('page.albums.restrictedUpload')}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!isSelectionMode && album.items.length > 0 && (
              <Button
                variant="secondary"
                onClick={() => setIsSelectionMode(true)}
                className="rounded-full p-3 text-sm">
                <FaCheck className="h-4 w-4" />
                {t('general.select')}
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={handleDownloadAlbum}
              className="rounded-full p-3"
              disabled={isDownloading}>
              <FaDownload className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              onClick={() => setEditModalOpen(true)}
              className="rounded-full p-3">
              <FaEdit className="h-4 w-4" />
            </Button>
            <Button
              variant="danger-v2"
              onClick={() => setDeleteModalOpen(true)}
              className="rounded-full p-3">
              <FaTrash className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {isSelectionMode && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-primary-900">
                {selectedPhotoIds.length > 0
                  ? t('page.photoAlbum.selection.selectedCount', { count: selectedPhotoIds.length })
                  : t('page.photoAlbum.selection.noSelection')}
              </span>
              {album.items.length > 0 && (
                <Button variant="secondary" onClick={handleSelectAll} className="text-sm">
                  {selectedPhotoIds.length === album.items.length
                    ? t('page.photoAlbum.selection.deselectAll')
                    : t('page.photoAlbum.selection.selectAll')}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {selectedPhotoIds.length > 0 && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => setMovePhotosModalOpen(true)}
                    className="flex items-center gap-2">
                    <FaArrowRightArrowLeft className="h-4 w-4" />
                    {t('page.photoAlbum.movePhotos.action')}
                  </Button>
                  <Button
                    variant="danger-v2"
                    onClick={() => setDeletePhotosModalOpen(true)}
                    className="flex items-center gap-2">
                    <FaTrash className="h-4 w-4" />
                    {t('page.photoAlbum.deletePhotos.action')}
                  </Button>
                </>
              )}
              <Button variant="secondary" onClick={handleClearSelection}>
                {t('general.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {album.items.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {album.items.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                showMetadata={album.openToUpload}
                onClick={handlePhotoClick}
                isSelected={selectedPhotoIds.includes(photo.id)}
                onSelectionChange={handleSelectionChange}
                isSelectionMode={isSelectionMode}
              />
            ))}
          </div>

          {totalPhotos > PHOTOS_PER_PAGE && (
            <div className="mt-8 flex flex-col items-center gap-4">
              {totalPages > 1 && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    type="button"
                    className="flex items-center justify-center rounded-md bg-secondary-200 p-1.5 text-primary-900 hover:opacity-75 disabled:opacity-25">
                    <TbChevronLeft />
                  </button>
                  <span>
                    {t('component.pagination.pageOf', { page: currentPage, total: totalPages })}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    type="button"
                    className="flex items-center justify-center rounded-md bg-secondary-200 p-1.5 text-primary-900 hover:opacity-75 disabled:opacity-25">
                    <TbChevronRight />
                  </button>
                </div>
              )}
              <p className="text-sm text-gray-500">
                {t('page.photoAlbum.showingPhotos', {
                  showing: displayedPhotos,
                  total: totalPhotos,
                })}
              </p>
            </div>
          )}
        </>
      ) : (
        <EmptyScreenView
          title={t('page.photoAlbum.empty.title')}
          subtitle={t('page.photoAlbum.empty.subtitle')}
        />
      )}

      <EditAlbumModal
        open={editModalOpen}
        setOpen={setEditModalOpen}
        album={album}
        onSave={handleAlbumUpdate}
      />

      <AddPhotosModal
        open={addPhotosModalOpen}
        setOpen={setAddPhotosModalOpen}
        albumId={album.id}
        onPhotosAdded={handlePhotosAdded}
      />

      <PhotoViewerModal
        open={photoViewerOpen}
        showMetadata={album.openToUpload}
        setOpen={setPhotoViewerOpen}
        photo={selectedPhoto}
        currentAlbumId={album.id}
        onDelete={handlePhotoDelete}
        onMove={handlePhotosMove}
      />

      <MovePhotoModal
        open={movePhotosModalOpen}
        setOpen={setMovePhotosModalOpen}
        photoIds={selectedPhotoIds}
        currentAlbumId={album.id}
        onPhotoMoved={handlePhotosMove}
      />

      <AreYouSureModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        title={t('page.photoAlbum.deleteAlbum.title')}
        description={t('page.photoAlbum.deleteAlbum.description', { albumTitle: album.title })}
        onConfirm={handleAlbumDelete}
        saveClassName="bg-danger text-secondary-50"
      />

      <AreYouSureModal
        open={deletePhotosModalOpen}
        setOpen={setDeletePhotosModalOpen}
        title={t('page.photoAlbum.deletePhotos.title')}
        description={t('page.photoAlbum.deletePhotos.description', {
          count: selectedPhotoIds.length,
        })}
        onConfirm={handleDeleteSelectedPhotos}
        saveClassName="bg-danger text-secondary-50"
      />
    </>
  );
}
